// frontend/src/components/CreatePost/PhotoUploader.jsx (versión simplificada)
import { useState, useRef } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import imageCompression from "browser-image-compression";

export default function PhotoUploader({ formData, setFormData, errors }) {
  const [previews, setPreviews] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handlePhotos = async (filesArray) => {
    const currentCount = formData.fotos.length;
    const totalAfterAdd = currentCount + filesArray.length;
    
    if (totalAfterAdd > 5) {
      alert(`Máximo 5 fotos por anúncio. Você já tem ${currentCount} foto(s).`);
      return;
    }

    setIsProcessing(true);

    const newFiles = [];
    const newPreviews = [];

    for (let file of filesArray) {
      if (!file.type.startsWith('image/')) {
        alert(`Arquivo ${file.name} não é uma imagem válida.`);
        continue;
      }

      if (file.size > 20 * 1024 * 1024) {
        alert(`Arquivo ${file.name} é muito grande. Máximo 20MB.`);
        continue;
      }

      let processedFile = file;
      if (file.size > 1 * 1024 * 1024) {
        try {
          processedFile = await imageCompression(file, { 
            maxSizeMB: 1, 
            maxWidthOrHeight: 1920, 
            useWebWorker: true 
          });
        } catch (err) { 
          console.error('Erro ao comprimir imagem:', err); 
        }
      }

      newFiles.push(processedFile);
      newPreviews.push(URL.createObjectURL(processedFile));
    }

    setFormData(prev => ({ 
      ...prev, 
      fotos: [...prev.fotos, ...newFiles] 
    }));
    setPreviews(prev => [...prev, ...newPreviews]);
    setIsProcessing(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handlePhotos(files);
    }
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    if (files.length > 0) handlePhotos(files);
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(previews[index]);
    
    setFormData(prev => ({ 
      ...prev, 
      fotos: prev.fotos.filter((_, i) => i !== index) 
    }));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        📷 Fotos <span className="text-red-500">*</span>
        <span className="text-xs text-gray-400 ml-1">(máximo 5)</span>
      </label>

      <div className="flex justify-between items-center mb-2">
        <div className="text-xs text-gray-500">
          {formData.fotos.length}/5 fotos selecionadas
          {formData.fotos.length === 5 && (
            <span className="text-amber-600 ml-2">⚠️ Limite máximo atingido</span>
          )}
        </div>
        {isProcessing && (
          <div className="text-xs text-[#449779] flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            Processando imagens...
          </div>
        )}
      </div>

      <div
        onClick={() => {
          if (formData.fotos.length < 5 && !isProcessing) {
            fileInputRef.current?.click();
          } else if (formData.fotos.length >= 5) {
            alert("Limite de 5 fotos atingido.");
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
          formData.fotos.length >= 5 || isProcessing
            ? "border-gray-300 bg-gray-100 cursor-not-allowed opacity-60" 
            : isDragging 
              ? "border-[#449779] bg-green-50" 
              : errors.fotos 
                ? "border-red-500 bg-red-50" 
                : "border-gray-300 bg-gray-50 hover:border-[#449779] hover:bg-green-50"
        }`}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          multiple
          onChange={handleFileChange}
          className="hidden"
          disabled={formData.fotos.length >= 5 || isProcessing}
        />
        {isProcessing ? (
          <>
            <Loader2 className="w-8 h-8 mx-auto mb-2 text-[#449779] animate-spin" />
            <p className="text-sm text-gray-500">Processando imagens...</p>
            <p className="text-xs text-gray-400 mt-1">Aguarde um momento</p>
          </>
        ) : (
          <>
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-500">
              {formData.fotos.length >= 5 
                ? "Limite de 5 fotos atingido" 
                : "Clique ou arraste suas fotos aqui"}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Formatos: JPG, PNG. Máx 2MB por foto
            </p>
          </>
        )}
      </div>
      {errors.fotos && <p className="text-red-500 text-xs mt-1">{errors.fotos}</p>}

      {previews.length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {previews.map((src, i) => (
            <div key={i} className="relative group">
              <img 
                src={src} 
                alt={`Preview ${i + 1}`} 
                className="w-full h-28 object-cover rounded-lg border border-gray-200"
              />
              <button
                type="button"
                onClick={() => removePhoto(i)}
                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1.5 opacity-90 hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
              <span className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                {i + 1}/{previews.length}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}